import { Tooltip } from '@mui/material';
import { FormField, FieldType } from './formFieldTypes';
import moment from 'moment';

export interface SubmissionAnswer {
  question_id: number;
  data: any;
  system_identifire: string;
  answer: any;
}

export interface SubmissionPayload {
  section_type: string;
  data: SubmissionAnswer[];
}

/**
 * Transform field values to submission format
 */
export const prepareSubmissionPayload = (
  fields: FormField[],
  selectedValues: Record<number, any>,
  sectionType: string
): SubmissionPayload => {
  const answerPayload: SubmissionAnswer[] = [];

  fields.forEach((field) => {
    const value = selectedValues[field.id];

    if (value === undefined || value === null || value === '') return;

    let data = value;
    let answer = value;

    // Handle special field types
    if (field.type === FieldType.RADIO || field.type === FieldType.CHECKBOX) {
      const selectedOption = field.options?.find((opt) => opt.answer === value);
      if (selectedOption) {
        data = selectedOption.id;
      }
    } else if (field.type === FieldType.DATE) {
      const m = moment(value, 'YYYY-MM-DD', true); // strict
      const dateOnly = m.isValid() ? m.format('YYYY-MM-DD') : value;
      data = dateOnly;
      answer = dateOnly;
    } else if (field.type === FieldType.SELECT) {
      const selectedOption = field.options?.find(
        (opt) => opt.answer === value || opt.id === value
      );
      if (selectedOption) {
        data = selectedOption.answer;
        answer = selectedOption.answer;
      }
    }

    answerPayload.push({
      question_id: field.id,
      data,
      system_identifire: field.system_identifire,
      answer,
    });
  });

  return { section_type: sectionType, data: answerPayload };
};

/**
 * Create form data for file uploads (multipart)
 */
export const prepareFormDataPayload = (
  fields: FormField[],
  selectedValues: Record<number, any>,
  sectionType: string
): FormData => {
  const formData = new FormData();

  const processedData = fields
    .map((field) => {
      const value = selectedValues[field.id];

      if (!value || value === '') {
        return null;
      }

      // Handle file uploads
      if (field.type === FieldType.FILE_UPLOAD && value instanceof FileList) {
        const file = value[0];
        if (file instanceof File) {
          formData.append(`file_${field.id}`, file);
          return {
            question_id: field.id,
            data: file.name,
            system_identifire: field.system_identifire,
            answer: file.name,
          };
        }
      }

      // Handle other types
      let data = value;
      let answer = value;

      if (field.type === FieldType.RADIO || field.type === FieldType.CHECKBOX) {
        const selectedOption = field.options?.find((opt) => opt.answer === value);
        if (selectedOption) {
          data = selectedOption.id;
        }
      } else if (field.type === FieldType.DATE) {
        const date = new Date(value);
        data = date.toISOString();
        answer = date.toISOString();
      } else if (field.type === FieldType.SELECT) {
        const selectedOption = field.options?.find(
          (opt) => opt.answer === value || opt.id === value
        );
        if (selectedOption) {
          data = selectedOption.answer;
          answer = selectedOption.answer;
        }
      }

      return {
        question_id: field.id,
        data,
        system_identifire: field.system_identifire,
        answer,
      };
    })
    .filter((item) => item !== null);

  const jsonPayload = {
    section_type: sectionType,
    data: processedData,
  };

  formData.append('payload', JSON.stringify(jsonPayload));
  return formData;
};

/**
 * Set prefilled values from API response
 */
export const setPrefilledValues = (
  fields: FormField[],
  answerList: Array<{ question_id: number; answer: any;[key: string]: any }>
): Record<number, any> => {
  const preFilledValues: Record<number, any> = {};

  fields.forEach((field) => {
    const answer = answerList.find((ans) => ans.question_id === field.id);

    if (answer) {
      let value = answer.answer;

      if (field.type === FieldType.DATE && answer.answer) {
        const m = moment(answer.answer);
        value = m.isValid() ? m.format('YYYY-MM-DD') : null;
      }

      preFilledValues[field.id] = value;
    }
  });

  return preFilledValues;
};

/**
 * Extract only displayable fields from section
 */
export const getDisplayableFields = (fields: FormField[]): FormField[] => {
  return fields.filter((field) => field.is_display === 1);
};

/**
 * Group fields by parent for structured rendering
 */
export const groupFieldsByParent = (
  fields: FormField[]
): Record<number | 'parent', FormField[]> => {
  const grouped: Record<number | 'parent', FormField[]> = {
    parent: [],
  };

  fields.forEach((field) => {
    if (!field.parent_question_id) {
      grouped['parent'].push(field);
    } else {
      if (!grouped[field.parent_question_id]) {
        grouped[field.parent_question_id] = [];
      }
      grouped[field.parent_question_id].push(field);
    }
  });

  return grouped;
};

/**
 * Check if form data has required answers for submission
 */
export const isFormComplete = (
  fields: FormField[],
  selectedValues: Record<number, any>
): boolean => {
  return fields.every((field) => {
    if (field.isOptional || field.type === FieldType.LINK) {
      return true;
    }

    const value = selectedValues[field.id];
    return value !== undefined && value !== null && value !== '';
  });
};

/**
 * Get field as flat object for easier access
 */
export const flattenFields = (fields: FormField[]): Record<number, FormField> => {
  const flattened: Record<number, FormField> = {};

  fields.forEach((field) => {
    flattened[field.id] = field;
  });

  return flattened;
};

/**
 * Convert field answer to display format
 */
export const formatAnswerForDisplay = (
  field: FormField,
  value: any
): string => {
  if (!value) {
    return '-';
  }

  if (field.type === FieldType.DATE) {
    return new Date(value).toLocaleDateString();
  }

  if (field.type === FieldType.SELECT || field.type === FieldType.RADIO) {
    const option = field.options?.find((opt) => opt.answer === value || opt.id === value);
    return option?.title || value;
  }

  if (field.type === FieldType.CHECKBOX) {
    return value ? 'Yes' : 'No';
  }

  return String(value);
};

/**
 * Renders a form field label with required indicator and hint tooltip
 * @param field - The form field object
 * @param hideLabel - Whether to hide the label
 * @returns JSX element or null
 */
export const renderFieldLabel = (field: FormField, hideLabel: boolean = false): React.ReactNode => {
  if (hideLabel) return null;

  return (
    <label className='text-secondary' style={{ marginBottom: '10px', display: 'block' }}>
      <span>
        {field.title}
        {!field.isOptional && (
          <sup style={{ marginLeft: '3px' }}>
            <i
              className='fa-solid fa-star-of-life'
              style={{ color: '#fa0000', fontSize: '0.5rem' }}
            ></i>
          </sup>
        )}
        {renderDynamicHint(field)}
      </span>
    </label>
  );
};

/**
 * Renders dynamic hint text from API
 * @param field - The form field object
 * @param showDynamicHint - Whether to show the dynamic hint
 * @returns JSX element or null
 */
export const renderDynamicHint = (field: FormField, showDynamicHint: boolean = true): React.ReactNode => {

  if (!showDynamicHint || (!field.hint && !field.dynamicHint)) {
    return null;
  }

  return (
    <Tooltip title={field.dynamicHint || field.hint} arrow>
      <i
        className='fa-solid fa-info-circle'
        style={{ color: '#FFFFFF', marginLeft: '5px', fontSize: '1rem', cursor: 'help' }}
      ></i>
    </Tooltip>
  );
};

/**
 * Renders error message display
 * @param errorMessage - The error message to display
 * @returns JSX element or null
 */
export const renderError = (errorMessage: string = ''): React.ReactNode => {
  if (!errorMessage) {
    return null;
  }

  return (
    <div
      style={{
        color: '#d32f2f',
        fontSize: '12px',
        marginTop: '6px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <i className='fa-solid fa-exclamation-circle' style={{ fontSize: '11px' }}></i>
      {errorMessage}
    </div>
  );
};

/**
 * Renders help text from field
 * @param field - The form field object
 * @returns JSX element or null
 */
export const renderHelpText = (field: FormField): React.ReactNode => {
  if (!field.help) {
    return null;
  }

  return (
    <div
      style={{
        color: '#8a92a2',
        fontSize: '11px',
        marginTop: '4px',
        fontStyle: 'italic',
      }}
    >
      {field.help}
    </div>
  );
};

/**
 * Determines if a child field should be visible based on parent field selection
 * Handles nested conditional fields (grandchildren) by recursively checking parent visibility
 */
export const shouldFieldBeVisible = (
  field: FormField,
  allFields: FormField[],
  selectedValues: Record<number, any>
): boolean => {
  // If no parent question ID, always show (it's a parent field)
  if (!field.parent_question_id) {
    return true;
  }

  // Find parent field
  const parentField = allFields.find((f) => f.id === field.parent_question_id);
  if (!parentField) {
    return false;
  }

  // Check if parent itself is visible (handles nested conditionals)
  if (!shouldFieldBeVisible(parentField, allFields, selectedValues)) {
    return false;
  }

  // Get parent's selected value
  const parentSelectedValue = selectedValues[parentField.id];

  // If parent has no value selected, hide child
  if (parentSelectedValue === undefined || parentSelectedValue === null || parentSelectedValue === '') {
    return false;
  }

  // If parent_option_id is specified, check if that specific option is selected
  if (field.parent_option_id) {
    return isOptionSelected(
      parentField,
      parentSelectedValue,
      field.parent_option_id
    );
  }

  // If no specific option ID, just check if parent has a value
  return true;
};

/**
 * Check if a specific option is selected in a parent field
 * Handles both simple values and object values
 */
const isOptionSelected = (
  parentField: FormField,
  selectedValue: any,
  targetOptionId: number
): boolean => {
  // Find the option with matching ID
  const matchedOption = parentField.options?.find((opt) => opt.id === targetOptionId);
  if (!matchedOption) {
    return false;
  }

  // Get the answer value from the option
  const optionAnswer = matchedOption.answer;

  // Get the actual selected value (handle object or primitive)
  let actualSelectedValue = selectedValue;
  if (typeof selectedValue === 'object' && selectedValue !== null) {
    actualSelectedValue = selectedValue.answer || selectedValue.value;
  }

  // Convert both to string for comparison (handles boolean/string mismatches)
  const optionAnswerStr = String(optionAnswer);
  const selectedValueStr = String(actualSelectedValue);

  return optionAnswerStr === selectedValueStr;
};

/**
 * Get all visible fields for current step (including visible children)
 * This is the main function to use when rendering fields
 */
export const getVisibleFields = (
  allFields: FormField[],
  selectedValues: Record<number, any>
): FormField[] => {
  return allFields.filter((field) =>
    shouldFieldBeVisible(field, allFields, selectedValues)
  );
};

/**
 * Get only parent-level fields (no children)
 */
export const getParentFields = (fields: FormField[]): FormField[] => {
  return fields.filter((field) => !field.parent_question_id);
};

/**
 * Get child fields of a specific parent
 */
export const getChildFields = (
  fields: FormField[],
  parentId: number
): FormField[] => {
  return fields.filter((field) => field.parent_question_id === parentId);
};

/**
 * Get all visible parent fields and their visible children
 */
export const getVisibleParentFieldsWithChildren = (
  allFields: FormField[],
  selectedValues: Record<number, any>
): FormField[] => {
  const visibleFields: FormField[] = [];

  // Add all parent fields
  const parentFields = getParentFields(allFields);
  parentFields.forEach((field) => {
    visibleFields.push(field);

    // Add their visible children
    const children = getChildFields(allFields, field.id);
    children.forEach((child) => {
      if (shouldFieldBeVisible(child, allFields, selectedValues)) {
        visibleFields.push(child);
      }
    });
  });

  return visibleFields;
};

/**
 * Check if a field has dependent children
 */
export const hasChildFields = (field: FormField, allFields: FormField[]): boolean => {
  return allFields.some((f) => f.parent_question_id === field.id);
};

/**
 * Get all possible related fields (current field + all its children recursively)
 */
export const getAllRelatedFields = (
  fieldId: number,
  allFields: FormField[]
): FormField[] => {
  const related: FormField[] = [];
  const queue = [fieldId];

  while (queue.length > 0) {
    const currentId = queue.shift();
    const field = allFields.find((f) => f.id === currentId);

    if (field && !related.find((f) => f.id === field.id)) {
      related.push(field);

      // Add all children to queue
      const children = getChildFields(allFields, field.id);
      children.forEach((child) => {
        queue.push(child.id);
      });
    }
  }

  return related;
};

/**
 * Filter fields for a specific section
 */
export const getFieldsBySection = (
  fields: FormField[],
  sectionType: string
): FormField[] => {
  return fields.filter((field) => field.section_type === sectionType);
};

/**
 * Sort fields by position
 */
export const sortFieldsByPosition = (fields: FormField[]): FormField[] => {
  return [...fields].sort((a, b) => a.position - b.position);
};

/**
 * Get fields to render in current view (parent + visible children)
 * Sorted by position
 */
export const getRenderableFields = (
  allFields: FormField[],
  selectedValues: Record<number, any>
): FormField[] => {
  const visibleFields = getVisibleFields(allFields, selectedValues);
  return sortFieldsByPosition(visibleFields);
};

/**
 * Check if form step has any visible fields with unsatisfied dependencies
 * (useful for determining if a step can be submitted)
 */
export const hasUnmetDependencies = (
  allFields: FormField[],
  selectedValues: Record<number, any>
): boolean => {
  const parentFields = getParentFields(allFields);

  for (const parent of parentFields) {
    const parentValue = selectedValues[parent.id];
    const children = getChildFields(allFields, parent.id);

    for (const child of children) {
      if (shouldFieldBeVisible(child, allFields, selectedValues)) {
        // This child is visible, check if it has a value
        const childValue = selectedValues[child.id];
        if (!child.isOptional && (!childValue || childValue === '')) {
          return true; // Found unmet dependency
        }
      }
    }
  }

  return false;
};

/**
 * Merge selectedValues with field values for submission
 * Ensures consistent data format
 */
export const normalizeFieldValues = (
  fields: FormField[],
  selectedValues: Record<number, any>
): Record<number, any> => {
  const normalized: Record<number, any> = {};

  fields.forEach((field) => {
    const value = selectedValues[field.id];

    if (value !== undefined && value !== null && value !== '') {
      // Handle special cases
      if (field.type === FieldType.CHECKBOX) {
        normalized[field.id] = Boolean(value);
      } else if (field.type === FieldType.DATE) {
        normalized[field.id] = value instanceof Date ? value : new Date(value);
      } else if (field.type === FieldType.SELECT && field.options) {
        const selected = field.options.find((opt) => opt.answer === value || opt.id === value);
        normalized[field.id] = selected ? selected.answer : value;
      } else {
        normalized[field.id] = value;
      }
    }
  });

  return normalized;
};
